/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Children, cloneElement, forwardRef, isValidElement } from 'react';
import type { FC, HTMLProps, RefAttributes } from 'react';

import VisuallyHidden from './visually-hidden';
import { rhythm } from './styles/typography';

type OwnProps = {
  href: string;
  label: string;
};

type ContactLinkProps = OwnProps & RefAttributes<HTMLAnchorElement> & HTMLProps<HTMLAnchorElement>;

const ContactLink: FC<ContactLinkProps> = forwardRef<HTMLAnchorElement, OwnProps>(
  ({ children, href, label, ...anchorProps }, ref) => {
    const clones = Children.map(children, (v) => {
      if (!isValidElement(v)) {
        return null;
      }

      return cloneElement(v, { 'aria-hidden': true });
    });

    return (
      <div
        css={css`
          border-radius: 50%;
          display: flex;
          justify-content: center;
          align-items: center;
          min-width: 2.5em;
          min-height: 2.5em;

          @media screen and (min-width: 60em) {
            min-width: initial;
            min-height: initial;
            margin-right: 0.5em;

            &:last-of-type {
              margin: 0;
            }
          }
        `}
      >
        <a
          css={css`
            margin: 0 auto;
            font-size: 1.4em;
            line-height: 1;
            margin: 0 ${rhythm(0.3)} 0 0;

            @media screen and (min-width: 60em) {
              & {
                font-size: 1em;
              }
            }

            svg {
              transition: color 0.2s ease-out;
              color: #aaa;
            }

            &:hover svg {
              color: #666;
            }
          `}
          {...anchorProps}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
          href={href}
          title={label}
        >
          {clones}
          <VisuallyHidden>{label}</VisuallyHidden>
        </a>
      </div>
    );
  },
);

ContactLink.displayName = 'ContactLink';

export default ContactLink;
